import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Supplement } from '../types';

type SupplementsByItem = Record<string, Supplement[]>;

const CACHE_KEY = 'menu_supplements_cache';

/**
 * Loads Hype-synced supplements (add-ons) grouped by the menu item id
 * (menu_supplements.article_id === menu_items.id). The supplement `id` is the
 * mapped_id that the edge function reports to HES, so it can be sent back in
 * order payloads and translated to Hype's supplement id.
 */
export function useSupplements() {
  const [supplementsByItem, setSupplementsByItem] = useState<SupplementsByItem>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed && typeof parsed === 'object') {
              setSupplementsByItem(parsed);
              setLoading(false);
            }
          } catch {
            // ignore broken cache
          }
        }

        const { data, error } = await supabase
          .from('menu_supplements')
          .select('id, mapped_id, name, price, article_id');

        if (error) throw error;

        const grouped: SupplementsByItem = {};
        for (const row of data ?? []) {
          const articleId = row.article_id;
          if (!articleId) continue;
          const supplement: Supplement = {
            id: String(row.mapped_id ?? row.id),
            name: row.name ?? 'Добавка',
            price: Number(row.price ?? 0),
            quantity: 1,
          };
          (grouped[articleId] ??= []).push(supplement);
        }

        if (!alive) return;
        setSupplementsByItem(grouped);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(grouped));
      } catch (err) {
        // Non-fatal: items without supplements still work.
        console.error('Could not load supplements:', err);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  return { supplementsByItem, loading };
}
